import React, { useState } from "react";
import { toast } from "react-toastify";
import { api } from "../../utils/api";
import { API_ENDPOINTS } from "../../constants/api";
import "./ClientFileUpload.css";

function ClientFileUpload({ onBack, onUploadComplete }) {
  // File upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientBrief, setClientBrief] = useState("");

  // File upload functions
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addFiles(files);
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const files = Array.from(event.dataTransfer.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    const validFiles = files.filter((file) => {
      const validTypes = ["video/mp4", "image/png", "image/jpeg", "image/jpg"];
      return validTypes.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      toast.warning(
        "Some files were skipped. Only MP4 videos and PNG/JPG images are supported."
      );
    }

    const filesWithPreview = validFiles.map((file) => ({
      file,
      id: Math.random().toString(36).substring(7),
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file),
      customName: "",
    }));

    setSelectedFiles((prev) => [...prev, ...filesWithPreview]);
  };

  const removeFile = (id) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      const removed = prev.find((f) => f.id === id);
      if (removed && removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Validation functions
  const validateUpload = () => {
    // Check if client brief is filled
    if (!clientBrief.trim()) {
      toast.error("Please fill in the client brief before uploading");
      return false;
    }

    // Check if all files have custom names
    const filesWithoutCustomNames = selectedFiles.filter(
      (file) => !file.customName.trim()
    );

    if (filesWithoutCustomNames.length > 0) {
      toast.error(
        `Please provide custom names for all files. ${filesWithoutCustomNames.length} file(s) missing custom names.`
      );
      return false;
    }

    return true;
  };

  const isUploadReady = () => {
    return (
      clientName.trim() &&
      clientBrief.trim() &&
      selectedFiles.length > 0 &&
      selectedFiles.every((file) => file.customName.trim())
    );
  };

  // Helper function to add delay between requests
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleUploadClient = async () => {
    if (!validateUpload()) {
      return;
    }

    setUploading(true);

    try {
      const loadingToast = toast.loading(
        "Uploading client files... Please do not close this page",
        { position: "top-right" }
      );

      const folderName = clientName.split(" ").join("_");

      // Create S3 signed URLs for each file with delay between requests
      const results = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        const fileData = selectedFiles[i];

        if (i > 0) {
          // Add 1000ms delay between requests (except for the first one)
          await delay(1000);
        }

        const result = await api.post(API_ENDPOINTS.FILES.GET_SIGNED_URL, {
          fileName: fileData.name,
          fileType: fileData.type,
          folderName,
        });

        results.push(result);
      }

      // Upload to S3 with delay between uploads
      const pushResults = [];
      for (let i = 0; i < results.length; i++) {
        const res = results[i];
        const fileData = selectedFiles[i];
        const file = fileData.file;
        const url = res.data.url;

        if (i > 0) {
          // Add 500ms delay between S3 uploads (except for the first one)
          await delay(500);
        }

        const uploadResult = await fetch(url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });

        pushResults.push(uploadResult);
      }
      // check if all push results are ok
      const allUploadsSuccessful = pushResults.every((res) => res.ok);
      if (!allUploadsSuccessful) {
        // toast notify and exit
        toast.dismiss(loadingToast);
        toast.error(
          "One or more file uploads to S3 failed. Please try again.",
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        setUploading(false);
        return;
      }

      // Update the database with the correct structure
      const data = {
        client_slides: selectedFiles.map((file, index) => ({
          location: `${process.env.REACT_APP_S3_LINK}${folderName}/${file.name}`,
          description: file.customName,
        })),
        client_name: clientName,
        client_brief: clientBrief,
      };

      const dbResponse = await api.post(
        API_ENDPOINTS.CLIENTS.CREATE_UPDATE,
        data
      );

      // check if all database response is ok
      if (!dbResponse.data.success) {
        toast.dismiss(loadingToast);
        toast.error(
          "Failed to update database with file information. Please try again.",
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        setUploading(false);
        return;
      }

      // Clear files after successful upload
      selectedFiles.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setSelectedFiles([]);
      setClientName("");
      setClientBrief("");

      toast.dismiss(loadingToast);
      toast.success(
        `Successfully uploaded ${selectedFiles.length} files for "${clientName}"!`,
        { position: "top-right", autoClose: 3000 }
      );

      // Notify parent component of successful upload
      if (onUploadComplete) {
        onUploadComplete(clientName);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Upload failed: ${error.message}`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    // Clean up any object URLs
    selectedFiles.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setSelectedFiles([]);
    setClientName("");
    setClientBrief("");

    if (onBack) {
      onBack();
    }
  };

  return (
    <div className="client-upload">
      <div className="section-header">
        <button
          onClick={handleCancel}
          className="btn-back"
          disabled={uploading}>
          ← Back to Client List
        </button>
        <h2>Upload Client Files</h2>
      </div>

      {/* Client Information Form */}
      <div className="upload-client-info">
        <h3>Client Information</h3>
        <div className="client-info-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="uploadClientName">Client Name *</label>
              <input
                type="text"
                id="uploadClientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
                placeholder="Enter client name"
                disabled={uploading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="uploadClientBrief">Client Brief</label>
            <textarea
              id="uploadClientBrief"
              value={clientBrief}
              onChange={(e) => setClientBrief(e.target.value)}
              placeholder="Enter client brief or description"
              rows="3"
              disabled={uploading}
            />
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      <div className="file-upload-section">
        <h3>Upload Files</h3>
        <div
          className={`file-drop-area ${dragActive ? "drag-active" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}>
          <div className="file-drop-content">
            <div className="file-drop-icon">📁</div>
            <p className="file-drop-text">
              Drag and drop your files here, or{" "}
              <label
                htmlFor="file-input"
                className="file-input-label">
                browse
              </label>
            </p>
            <p className="file-drop-hint">
              Supports MP4 videos and PNG/JPG images
            </p>
            <input
              id="file-input"
              type="file"
              multiple
              accept=".mp4,.png,.jpg,.jpeg"
              onChange={handleFileSelect}
              className="file-input-hidden"
              disabled={uploading}
            />
          </div>
        </div>
      </div>

      {/* File Preview Section */}
      {selectedFiles.length > 0 && (
        <div className="file-preview-section">
          <h4>Selected Files ({selectedFiles.length})</h4>
          <div className="file-preview-grid">
            {selectedFiles.map((fileObj) => (
              <div
                key={fileObj.id}
                className="file-preview-item">
                <div className="file-preview-thumbnail">
                  {fileObj.type.startsWith("image/") ? (
                    <img
                      src={fileObj.preview}
                      alt={fileObj.name}
                      className="file-thumbnail-media"
                    />
                  ) : (
                    <video
                      src={fileObj.preview}
                      className="file-thumbnail-media"
                      muted
                    />
                  )}
                </div>
                <div className="file-preview-info">
                  <p className="file-preview-name">{fileObj.name}</p>
                  <p className="file-preview-size">
                    {formatFileSize(fileObj.size)}
                  </p>
                  <input
                    type="text"
                    placeholder="Custom name (optional)"
                    value={fileObj.customName}
                    onChange={(e) => {
                      setSelectedFiles((prev) =>
                        prev.map((f) =>
                          f.id === fileObj.id
                            ? { ...f, customName: e.target.value }
                            : f
                        )
                      );
                    }}
                    className="file-custom-name"
                    disabled={uploading}
                  />
                </div>
                <button
                  className="file-remove-btn"
                  onClick={() => removeFile(fileObj.id)}
                  aria-label="Remove file"
                  disabled={uploading}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Controls */}
      <div className="upload-controls">
        <button
          className="btn-secondary"
          onClick={handleCancel}
          disabled={uploading}>
          Cancel
        </button>

        {selectedFiles.length > 0 && (
          <button
            className="btn-secondary clear-btn"
            onClick={() => {
              selectedFiles.forEach((f) => {
                if (f.preview) URL.revokeObjectURL(f.preview);
              });
              setSelectedFiles([]);
            }}
            disabled={uploading}>
            Clear Files
          </button>
        )}

        <button
          className="btn-primary upload-btn"
          onClick={handleUploadClient}
          disabled={uploading || !isUploadReady()}>
          {uploading
            ? "Uploading..."
            : `Upload ${selectedFiles.length} File${
                selectedFiles.length !== 1 ? "s" : ""
              }`}
        </button>
      </div>
    </div>
  );
}

export default ClientFileUpload;
